import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { CardHeader } from "@/components/organisms/SBCs/CardHeader";
import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import Card from "@/design-system/Card/Card";
import { cn } from "@/lib/utils";
import { Tier } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";

import { SBCRequirements } from "./SBCRequirements";

const EYEBROW =
  "text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-faint";

type Props = {
  name: string;
  imageURL: string;
  reward?: SBCRewardType;
  rewardUrl: string;
  tier: Tier;
  description?: string;
  requirements: string[];
  /** The cheapest cart of base cards, shown beside the requirements. */
  suggestedSolution?: React.ReactNode;
  /**
   * Put the cart first. A Streamline challenge asks for a score rather than a
   * squad, so its one requirement is a footnote and the cards are the subject.
   */
  leadWithSolution?: boolean;
  formattedPrice: string;
  pointsRequired?: number;
  sbcToggle: React.ReactNode;
  solveAction?: React.ReactNode;
  isCompleted?: boolean;
};

/**
 * One challenge, read top to bottom: what it is and what it costs, what it
 * asks for, what it pays, then the two things you can do about it. The reward
 * is pinned to the foot by the spacer, so a row of tiles lines its bands up
 * however long the requirements run.
 */
export const SBCTile = ({
  name,
  imageURL,
  reward,
  rewardUrl,
  tier,
  description,
  requirements,
  suggestedSolution,
  leadWithSolution,
  formattedPrice,
  pointsRequired,
  sbcToggle,
  solveAction,
  isCompleted,
}: Props) => {
  const { t } = useTranslation("sbcs");

  /* Whichever leads takes the free width; the other sizes to its content. */
  const requirementsBlock = (
    <div
      className={cn(
        "flex flex-col gap-y-2",
        leadWithSolution ? "min-w-0" : "min-w-0 flex-1",
      )}
    >
      <span className={EYEBROW}>{t("sbcCard.requirements")}</span>
      <SBCRequirements requirements={requirements} />
    </div>
  );

  const solutionBlock = suggestedSolution && (
    <div
      className={cn(
        "flex flex-col gap-y-2",
        leadWithSolution ? "min-w-0 flex-1" : "shrink-0",
      )}
    >
      <span className={EYEBROW}>{t("sbcCard.suggestedSolution")}</span>
      {suggestedSolution}
    </div>
  );

  return (
    <Card
      hoverable={false}
      className={cn(
        "flex h-full flex-col overflow-hidden transition-opacity",
        /* Done, and still here to be undone: legible, but not competing with
           the challenges that are left. */
        isCompleted && "opacity-50",
      )}
    >
      <CardHeader
        imageURL={imageURL}
        name={name}
        price={formattedPrice}
        pointsRequired={pointsRequired}
        trailing={sbcToggle}
      />

      <div className="flex gap-x-5 px-5 pb-5">
        {leadWithSolution ? (
          <>
            {solutionBlock}
            {requirementsBlock}
          </>
        ) : (
          <>
            {requirementsBlock}
            {solutionBlock}
          </>
        )}
      </div>

      <div className="flex-1" />

      <RewardBand
        reward={reward}
        rewardUrl={rewardUrl}
        description={description}
        tier={tier}
      />

      {/* The action takes the whole foot of the card: there is one thing to do
          with a challenge, and it gets the width to say so. It keeps the
          button's own shape inside that width rather than becoming a rail. */}
      <div className="px-5 pb-5">{solveAction}</div>
    </Card>
  );
};
